import { FC, useCallback, useMemo, useEffect } from "react"
import classNames from "classnames"
import { useDispatch, useSelector } from "react-redux"

// components
import { TabContent } from "./TabContent"
import { TabItem } from "./TabItem"
import { PersonalizationSection } from "../../PersonalizationSection/PersonalizationSection"

// redux
import { RootState } from "../../../store/store"
import { setAudience } from "../../../store/slices/audience.slice"

// hooks
import { usePersonalizationComponent } from "../../../hooks/usePersonalizationComponent"

// styles
import * as TabsStyles from "./Tabs.module.scss"
import { homepageTabUl } from "../Tabs/TabItem.module.scss"
import * as globalStyles from "../../../theme/global.module.scss"

// types
import { BaseState, setMetaData } from "../../../store/slices/metadata.slice"

type DefaultLandingPage = {
  readonly landingPageVariationInternalTitle: string | null
  readonly title: string | null
  readonly content: ReadonlyArray<
    | {
        readonly contentful_id: string
        readonly title: string | null
        readonly attendeeArticles: ReadonlyArray<{
          readonly id: string
          readonly slug: string | null
          readonly articleId: number | null
          readonly title: string | null
        } | null> | null
      }
    | {
        readonly title: string | null
        readonly attendeeCategories: ReadonlyArray<{
          readonly title: string | null
          readonly iconName: string | null
          readonly slug: string | null
        } | null> | null
      }
  >
}

type TicketProps = {
  noTicketsFoundActionComplex: Queries.HomepageDataQuery["noTicketsFoundActionComplex"]
  ticketsFoundActionComplex: Queries.HomepageDataQuery["ticketsFoundActionComplex"]
  messageNoUpcomingOrders: Queries.HomepageDataQuery["messageNoUpcomingOrders"]
  searchingForTickets: Queries.HomepageDataQuery["searchingForTickets"]
}

type EventProps = {
  noEventsFoundActionComplex: Queries.HomepageDataQuery["noEventsFoundActionComplex"]
  eventsFoundActionComplex: Queries.HomepageDataQuery["eventsFoundActionComplex"]
  searchingForEvents: Queries.HomepageDataQuery["searchingForEvents"]
  messageNoUpcomingEvents: Queries.HomepageDataQuery["messageNoUpcomingEvents"]
}

type LoginProps = {
  loginHeaderMicrocopy: Queries.HelpArticleQuery["loginHeaderMicrocopy"]
  loginContentMicrocopy: Queries.HelpArticleQuery["loginContentMicrocopy"]
  loginButtonMicrocopy: Queries.HelpArticleQuery["loginButtonMicrocopy"]
}

export type Props = {
  attendee: DefaultLandingPage
  dflt: DefaultLandingPage
  organizer: DefaultLandingPage
  prefixedBasePath: string
  logInMicroCopySetEnglish: Queries.CommonIssuesAndQuestionsQuery["logInMicroCopySetEnglish"]
  logInMicroCopySetNonEnglish: Queries.CommonIssuesAndQuestionsQuery["logInMicroCopySetNonEnglish"]
} & TicketProps &
  EventProps &
  LoginProps

export const getTabId = (keyValue: string | number) => `tab${keyValue}`
export const getPanelId = (keyValue: string | number) => `panel${keyValue}`

export const Tabs: FC<Props> = (props) => {
  const {
    attendee,
    dflt,
    organizer,
    prefixedBasePath,
    logInMicroCopySetEnglish,
    logInMicroCopySetNonEnglish,
    noTicketsFoundActionComplex,
    ticketsFoundActionComplex,
    messageNoUpcomingOrders,
    searchingForTickets,
    noEventsFoundActionComplex,
    eventsFoundActionComplex,
    searchingForEvents,
    messageNoUpcomingEvents,
    loginContentMicrocopy,
    loginHeaderMicrocopy,
    loginButtonMicrocopy
  } = props

  const audience = useSelector((state: RootState) => state.audience) as
    | string
    | null

  const dispatch = useDispatch()

  // Use custom hook fo personalization logic
  const { activePersonalizationComponent } = usePersonalizationComponent()

  /****
   * If audience is null when a user lands on the homepage,
   * set the value to the default
   * ****/
  useEffect(() => {
    audience ?? dispatch(setAudience(dflt?.title))
  }, [])

  const handleSelect = useCallback(
    (
      tabKey: string,
      audienceEnglishCategory: BaseState["audienceEnglishCategory"]
    ) => {
      dispatch(setAudience(tabKey))
      dispatch(
        setMetaData({
          audienceEnglishCategory
        })
      )
    },
    [dflt, attendee, organizer]
  )

  const tabItems = useMemo(() => {
    const items = []

    items.push(
      <TabItem
        key={attendee?.title}
        onSelected={handleSelect}
        displayName={attendee?.title ?? ""}
        isSelected={attendee?.title === audience}
        landingPageVariationInternalTitle={
          attendee?.landingPageVariationInternalTitle as
            | "Homepage variation: Attendee"
            | "Homepage variation: Organizer"
        }
      />
    )
    items.push(
      <TabItem
        key={organizer?.title}
        onSelected={handleSelect}
        displayName={organizer?.title ?? ""}
        isSelected={organizer?.title === audience}
        landingPageVariationInternalTitle={
          organizer?.landingPageVariationInternalTitle as
            | "Homepage variation: Attendee"
            | "Homepage variation: Organizer"
        }
      />
    )
    return items
  }, [attendee, organizer, audience, handleSelect])

  return (
    <>
      <nav
        className={classNames(
          globalStyles.edsLPadVert3,
          globalStyles.edsLMarBot3
        )}
      >
        <ul className={homepageTabUl}>{tabItems}</ul>
      </nav>
      <PersonalizationSection
        noTicketsFoundActionComplex={noTicketsFoundActionComplex}
        ticketsFoundActionComplex={ticketsFoundActionComplex}
        messageNoUpcomingOrders={messageNoUpcomingOrders}
        searchingForTickets={searchingForTickets}
        noEventsFoundActionComplex={noEventsFoundActionComplex}
        eventsFoundActionComplex={eventsFoundActionComplex}
        searchingForEvents={searchingForEvents}
        messageNoUpcomingEvents={messageNoUpcomingEvents}
        loginButtonMicrocopy={loginButtonMicrocopy}
        loginContentMicrocopy={loginContentMicrocopy}
        loginHeaderMicrocopy={loginHeaderMicrocopy}
        containerClassName={
          activePersonalizationComponent === "attendee"
            ? TabsStyles?.personalizationAttendeeContainer
            : activePersonalizationComponent === "organizer"
            ? TabsStyles?.personalizationOrganizerContainer
            : undefined
        }
      />
      <TabContent
        prefixedBasePath={prefixedBasePath}
        segment={audience === organizer?.title ? organizer : attendee}
        logInMicroCopySetEnglish={logInMicroCopySetEnglish}
        logInMicroCopySetNonEnglish={logInMicroCopySetNonEnglish}
      />
    </>
  )
}
